import { useRef, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  NutritionHubTasksBackfillJobFieldsFragment,
  useNutritionHubTasksBackfillStatusQuery,
  useStartNutritionHubTasksBackfillMutation,
  useStopNutritionHubTasksBackfillMutation,
} from "../../../types";
import { useSnackbar } from "../../../components/Snackbar/SnackbarContext";

const isInFlight = (status: string | null | undefined): boolean => status === "pending" || status === "inProgress";

const JobStatusCard = ({ job }: { job: NutritionHubTasksBackfillJobFieldsFragment }) => (
  <Alert severity={job.status === "failed" ? "error" : "info"}>
    <Typography variant="body2">
      Status: <strong>{job.status}</strong>
    </Typography>
    <Typography variant="body2">
      Athletes: {job.athleteProcessed} / {job.athleteTotal}
      {job.failedAthleteCount > 0 ? ` (${job.failedAthleteCount} skipped)` : ""}
    </Typography>
    <Typography variant="body2">Date floor: {job.dateFloor}</Typography>
    {job.lastAthleteId && <Typography variant="body2">Last athlete: {job.lastAthleteId}</Typography>}
    {job.errorMessage && <Typography variant="body2">Last error: {job.errorMessage}</Typography>}
    {(job.failedAthleteIds?.length ?? 0) > 0 && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" gutterBottom>
          Failed athlete IDs
          {job.failedAthleteCount > (job.failedAthleteIds?.length ?? 0)
            ? ` (showing ${job.failedAthleteIds?.length ?? 0} of ${job.failedAthleteCount})`
            : ""}
          — open each athlete’s goals and bump goal dates to re-trigger Hub sync:
        </Typography>
        <TextField
          value={(job.failedAthleteIds ?? []).join("\n")}
          fullWidth
          multiline
          minRows={Math.min(job.failedAthleteIds?.length ?? 0, 8)}
          maxRows={12}
          size="small"
          InputProps={{ readOnly: true }}
          sx={{ fontFamily: "monospace", "& textarea": { fontFamily: "monospace", fontSize: 12 } }}
        />
      </Box>
    )}
    <Typography variant="body2">Started: {new Date(job.startedAt).toLocaleString()}</Typography>
    {job.finishedAt && <Typography variant="body2">Finished: {new Date(job.finishedAt).toLocaleString()}</Typography>}
  </Alert>
);

export const NutritionHubTasksBackfillPage = () => {
  const [orgId, setOrgId] = useState("");
  const [statusOrgId, setStatusOrgId] = useState<string | null>(null);
  // Sole display source — set by Start / Stop / Refresh only (no Apollo merge fights).
  const [job, setJob] = useState<NutritionHubTasksBackfillJobFieldsFragment | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  /** Bumped on every Start/Stop/Refresh so a slower in-flight op cannot overwrite a newer result. */
  const jobOpGeneration = useRef(0);
  const { setMessage } = useSnackbar();

  const trimmedOrgId = orgId.trim();
  const inFlight = isInFlight(job?.status);
  // Stop the org on the card, not a typed field that may have changed mid-flight.
  const stopOrgId = job?.orgId ?? statusOrgId;

  const { loading, error: statusError, refetch } = useNutritionHubTasksBackfillStatusQuery({
    skip: !statusOrgId,
    variables: { orgId: statusOrgId ?? "" },
    errorPolicy: "all",
    fetchPolicy: "network-only",
  });

  const applyJobResult = (
    nextJob: NutritionHubTasksBackfillJobFieldsFragment | null,
    generation: number,
    orgIdFallback?: string
  ) => {
    if (generation !== jobOpGeneration.current) {
      return;
    }
    setJob(nextJob);
    if (nextJob?.orgId) {
      setStatusOrgId(nextJob.orgId);
    } else if (orgIdFallback) {
      setStatusOrgId(orgIdFallback);
    }
  };

  const [startBackfill, { loading: starting, error: startError }] = useStartNutritionHubTasksBackfillMutation({
    errorPolicy: "all",
    onCompleted: result => {
      const generation = ++jobOpGeneration.current;
      const started = result.startNutritionHubTasksBackfill;
      if (!started?.job) {
        setMessage("error", "Start returned no job payload");
        return;
      }
      applyJobResult(started.job, generation);
      if (started.alreadyRunning) {
        setMessage("info", "A backfill is already running for this org");
      } else {
        setMessage("success", "Backfill started");
      }
    },
    onError: error => setMessage("error", error.message),
  });

  const [stopBackfill, { loading: stopping, error: stopError }] = useStopNutritionHubTasksBackfillMutation({
    errorPolicy: "all",
    onCompleted: result => {
      const generation = ++jobOpGeneration.current;
      applyJobResult(result.stopNutritionHubTasksBackfill?.job ?? null, generation);
      setMessage("success", "Backfill stopped");
    },
    onError: error => setMessage("error", error.message),
  });

  const refreshStatus = async (orgIdToLoad: string) => {
    const generation = ++jobOpGeneration.current;
    setStatusOrgId(orgIdToLoad);
    setRefreshing(true);
    try {
      const result = await refetch({ orgId: orgIdToLoad });
      applyJobResult(result.data?.nutritionHubTasksBackfillStatus ?? null, generation, orgIdToLoad);
    } catch (error) {
      if (generation === jobOpGeneration.current) {
        setMessage("error", error instanceof Error ? error.message : "Failed to refresh status");
      }
    } finally {
      if (generation === jobOpGeneration.current) {
        setRefreshing(false);
      }
    }
  };

  const opsBusy = starting || stopping || refreshing;
  const graphqlError = statusError ?? startError ?? stopError;

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h4" gutterBottom>
        Nutrition Hub Tasks Backfill
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Slowly syncs today-and-forward nutrition goals to the Hub Tasks API for one org (one athlete every few seconds).
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Org ID"
          value={orgId}
          onChange={event => setOrgId(event.target.value)}
          fullWidth
          size="small"
          disabled={starting || inFlight || opsBusy}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={!trimmedOrgId || inFlight || opsBusy}
            onClick={() => {
              void startBackfill({ variables: { input: { orgId: trimmedOrgId } } });
            }}
          >
            Start
          </Button>
          <Button
            variant="outlined"
            color="warning"
            disabled={!stopOrgId || !inFlight || opsBusy}
            onClick={() => {
              if (!stopOrgId) {
                return;
              }
              void stopBackfill({ variables: { input: { orgId: stopOrgId } } });
            }}
          >
            Stop
          </Button>
        </Stack>

        {graphqlError && (
          <Alert severity="error">
            <Typography variant="body2">GraphQL error: {graphqlError.message}</Typography>
          </Alert>
        )}

        {job ? (
          <JobStatusCard job={job} />
        ) : (
          statusOrgId &&
          !loading &&
          !refreshing &&
          !graphqlError && <Alert severity="warning">No backfill status found in Redis for this org (TTL may have expired).</Alert>
        )}

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={!trimmedOrgId || loading || opsBusy}
          onClick={() => {
            void refreshStatus(trimmedOrgId);
          }}
          sx={{ alignSelf: "flex-start" }}
        >
          Refresh status
        </Button>
      </Stack>
    </Box>
  );
};
